import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { MdClose } from 'react-icons/md';
import { LuPanelLeft } from 'react-icons/lu';
import {
  IconButton,
  ICON_BUTTON_SIZE_TB,
  TOOLTIP_SIZE_MEDIUM,
  Tooltip,
} from '@aibuild/ui';
import { useSidebar } from '@contexts/useSidebar';
import { RAIL_TOOLTIP, SidebarExpandHandle } from '@components/SidebarRail';
import {
  Wrapper,
  Header,
  LogoLink,
  Logo,
  ImgLogo,
  CollapseSlot,
  Navigation,
  NavigationItemWrapper,
  NavigationItem,
  NavigationItemIcon,
  NavigationItemReactIcon,
  NavigationItemLabel,
  CustomNavigationItem,
  BottomNavigation,
  ExtraContentDivider,
  TextButton,
  MobileCloseButton,
} from './NavigationDrawer.styled';

const DrawerNavigationItem = ({ dataTestId, item, onClose, collapsed = false }) => {
  const IconComponent = item?.iconComponent;

  const iconElement = IconComponent ? (
    <NavigationItemReactIcon>
      <IconComponent size={22} />
    </NavigationItemReactIcon>
  ) : (
    <NavigationItemIcon name={item?.iconName} />
  );

  const content = (
    <>
      {iconElement}
      {!collapsed && <NavigationItemLabel>{item?.label}</NavigationItemLabel>}
    </>
  );

  const element = item.renderAs ? (
    <CustomNavigationItem
      as={item.renderAs}
      data-testid={`${dataTestId}__navigation_item`}
      onClick={() => { item.onClick?.(); onClose?.(); }}
      className={item.className}
      aria-label={collapsed ? item?.label : undefined}
      $collapsed={collapsed}
    >
      {content}
    </CustomNavigationItem>
  ) : (
    <NavigationItem
      as={item?.external ? 'a' : undefined}
      data-testid={`${dataTestId}__navigation_item`}
      to={item?.to || ''}
      href={item?.to || ''}
      end={item?.exact}
      target={item?.external ? '_blank' : undefined}
      rel={item?.external ? 'noopener noreferrer' : undefined}
      onClick={onClose}
      aria-label={collapsed ? item?.label : undefined}
      $collapsed={collapsed}
    >
      {content}
    </NavigationItem>
  );

  if (!collapsed) return element;

  return (
    <Tooltip content={item?.label} {...RAIL_TOOLTIP}>
      {element}
    </Tooltip>
  );
};

const itemShape = PropTypes.shape({
  id: PropTypes.string,
  iconName: PropTypes.string,
  iconComponent: PropTypes.elementType,
  label: PropTypes.string.isRequired,
  to: PropTypes.string,
  exact: PropTypes.bool,
  onClick: PropTypes.func,
  external: PropTypes.bool,
  renderAs: PropTypes.string,
  className: PropTypes.string,
});

DrawerNavigationItem.propTypes = {
  dataTestId: PropTypes.string.isRequired,
  onClose: PropTypes.func,
  collapsed: PropTypes.bool,
  item: itemShape.isRequired,
};

const NavigationDrawer = ({
  className,
  dataTestId = 'navigation-drawer',
  items,
  bottomItems,
  withLogo = true,
  logoUrl,
  subHeader,
  extraContent,
  collapsed = false,
  onCollapse,
  onExpand,
}) => {
  const intl = useIntl();
  const { closeMobile } = useSidebar();
  const collapseLabel = intl.formatMessage({
    id: 'nav.sidebar.collapse',
    defaultMessage: 'Collapse sidebar',
  });

  const renderItem = (item, key) => (
    <DrawerNavigationItem
      key={key}
      dataTestId={dataTestId}
      item={item}
      onClose={closeMobile}
      collapsed={collapsed}
    />
  );

  return (
    <Wrapper data-testid={dataTestId} className={className} $collapsed={collapsed}>
      {withLogo && (
        <Header $collapsed={collapsed}>
          <LogoLink data-testid={`${dataTestId}__logo-link`} to="/">
            {logoUrl ? <ImgLogo src={logoUrl} /> : <Logo />}
          </LogoLink>

          {onCollapse && !collapsed && (
            <CollapseSlot>
              <IconButton
                ariaLabel={collapseLabel}
                icon={<LuPanelLeft aria-hidden="true" />}
                size={ICON_BUTTON_SIZE_TB}
                onClick={onCollapse}
                tooltip={collapseLabel}
                tooltipSize={TOOLTIP_SIZE_MEDIUM}
                dataTestId={`${dataTestId}__collapse`}
              />
            </CollapseSlot>
          )}

          <MobileCloseButton
            onClick={closeMobile}
            aria-label="Close menu"
            data-testid="mobile-close-button"
          >
            <MdClose size={24} />
          </MobileCloseButton>
        </Header>
      )}

      {collapsed && onExpand && (
        <SidebarExpandHandle onExpand={onExpand} dataTestId={`${dataTestId}__expand`} />
      )}

      {subHeader}

      <Navigation $collapsed={collapsed}>
        {items.map((item) => {
          const key = `navigation-item-${item?.id || item?.label}`;
          if (!item.items) return renderItem(item, key);
          return (
            <NavigationItemWrapper key={key}>
              {item.items.map((subItem) =>
                renderItem(subItem, `${key}-sub-item-${subItem?.id || subItem?.label}`),
              )}
            </NavigationItemWrapper>
          );
        })}
      </Navigation>

      {extraContent && !collapsed && (
        <>
          <ExtraContentDivider />
          {extraContent}
        </>
      )}

      {bottomItems && bottomItems.length > 0 && (
        <BottomNavigation $collapsed={collapsed}>
          {bottomItems.map((item) =>
            item.to ? (
              renderItem(item, `navigation-item-bottom-${item?.id || item?.label}`)
            ) : (
              <TextButton
                key={`navigation-item-bottom-${item?.id || item?.label}`}
                onClick={item.onClick}
                type="button"
              >
                {item?.iconComponent && <item.iconComponent size={16} />}
                {item?.label}
              </TextButton>
            ),
          )}
        </BottomNavigation>
      )}
    </Wrapper>
  );
};

NavigationDrawer.propTypes = {
  dataTestId: PropTypes.string,
  className: PropTypes.string,
  items: PropTypes.oneOfType([
    PropTypes.arrayOf(itemShape),
    PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        label: PropTypes.string.isRequired,
        items: PropTypes.arrayOf(itemShape),
      }),
    ),
  ]).isRequired,
  bottomItems: PropTypes.arrayOf(itemShape),
  withLogo: PropTypes.bool,
  logoUrl: PropTypes.string,
  subHeader: PropTypes.node,
  extraContent: PropTypes.node,
  /** Icon-only rail: labels become tooltips and `extraContent` is hidden. */
  collapsed: PropTypes.bool,
  onCollapse: PropTypes.func,
  onExpand: PropTypes.func,
};

export default NavigationDrawer;
